import type {CodexNotification} from '@reados/contracts';
import {upstreamThreadId} from './event-state';

export interface StreamRecoveryNotice {
  ownerThreadId:string;
  threadId:string;
  turnId:string;
  phase:'retrying'|'failed';
  message:string;
}
export interface ConversationTurnError {
  ownerThreadId:string;
  threadId:string;
  turnId:string;
  message:string;
}
const object=(value:unknown):Record<string,unknown>=>value&&typeof value==='object'&&!Array.isArray(value)?value as Record<string,unknown>:{};
const failureMessage='回复连接中断，未能完成。请稍后重试。';
export const sandboxOomMessage='本次读取因当前套餐内存不足而中断，请购买更大内存套餐后重试。';
export type StreamTerminalStatus='completed'|'failed'|'interrupted';
export function streamEventTurnId(event:CodexNotification):string|undefined {
  const id=object(event.params.turn).id??event.params.turnId??event.params.turn_id;
  return typeof id==='string'&&id.length>0?id:undefined;
}

export function conversationEventErrorMessage(event:CodexNotification):string {
  const error=event.params.error;
  if(typeof error==='string'&&error)return error;
  const detail=object(error);
  for(const value of [detail.message,event.params.message,detail.additionalDetails,event.params.additionalDetails])if(typeof value==='string'&&value)return value;
  return '回复发生错误，请稍后重试。';
}

export function streamTerminalStatus(event:CodexNotification):StreamTerminalStatus|undefined {
  if(event.method==='turn/interrupted')return 'interrupted';
  if(event.method==='turn/failed')return 'failed';
  if(event.method==='reados/turnTerminal'&&['failed','interrupted'].includes(String(event.params.status)))return event.params.status as StreamTerminalStatus;
  if(event.method==='turn/completed'){
    const status=object(event.params.turn).status;
    return status==='failed'||status==='interrupted'?status:'completed';
  }
}

/** Persisted lifecycle order is authoritative even when every turn has ended. */
export function latestStreamHistoryTurn(history:CodexNotification[],nativeThreadId:string):string|undefined {
  let started:string|undefined,terminal:string|undefined;
  for(const event of history){
    if(upstreamThreadId(event)!==nativeThreadId)continue;
    const turnId=streamEventTurnId(event);if(!turnId)continue;
    if(event.method==='turn/started')started=turnId;
    else if(event.method.startsWith('turn/')&&streamTerminalStatus(event))terminal=turnId;
  }
  return started??terminal;
}

/** Recognize stock Codex stream retries, never arbitrary errors or provider text. */
export function streamErrorPresentation(event:CodexNotification):Pick<StreamRecoveryNotice,'phase'|'message'>|null {
  if(!['error','reados/error'].includes(event.method)||event.params.readosCapacityRetry===true)return null;
  const error=object(event.params.error),message=typeof event.params.error==='string'?event.params.error:typeof error.message==='string'?error.message:event.params.message;
  const info=error.codexErrorInfo??event.params.codexErrorInfo;
  const disconnected=info==='responseStreamDisconnected'||Object.hasOwn(object(info),'responseStreamDisconnected');
  const eof=typeof message==='string'&&/^stream disconnected before completion: stream closed before response\.completed\.?$/i.test(message.trim());
  const retry=typeof message==='string'?/^Reconnecting(?:\.\.\.|…)\s+(\d+)\/(\d+)$/.exec(message.trim()):null;
  if(!disconnected&&!eof&&!retry)return null;
  if(event.params.willRetry===true){
    const count=retry&&Number(retry[1]),max=retry&&Number(retry[2]);
    return {phase:'retrying',message:count&&max&&count<=max?`连接中断，正在重试（${count}/${max}）`:'连接中断，正在重试…'};
  }
  return {phase:'failed',message:failureMessage};
}

/** Native failures belong to their exact turn, independently of operation
 * errors. Replayed failures from an older turn cannot become today's banner. */
export function updateConversationTurnError(current:ConversationTurnError|null,event:CodexNotification,ownerThreadId:string,nativeThreadId?:string,activeTurnId?:string,knownTerminalStatus?:StreamTerminalStatus):ConversationTurnError|null {
  const previous=current?.ownerThreadId===ownerThreadId?current:null,threadId=upstreamThreadId(event),turnId=streamEventTurnId(event);
  if(!nativeThreadId||threadId!==nativeThreadId||!turnId||activeTurnId&&turnId!==activeTurnId||event.params.ownerThreadId!==undefined&&event.params.ownerThreadId!==ownerThreadId)return previous;
  if(['error','reados/error'].includes(event.method)){
    if(event.params.readosCapacityRetry===true||streamErrorPresentation(event)||knownTerminalStatus==='completed'||knownTerminalStatus==='interrupted'||knownTerminalStatus==='failed'&&event.params.willRetry===true)return previous;
    return {ownerThreadId,threadId,turnId,message:conversationEventErrorMessage(event)};
  }
  if(event.method==='turn/started'&&!knownTerminalStatus&&previous?.turnId!==turnId)return null;
  if(event.method==='reados/turnTerminal'&&event.params.reason==='sandbox-oom')return {ownerThreadId,threadId,turnId,message:sandboxOomMessage};
  if(!previous||previous.threadId!==threadId||previous.turnId!==turnId)return previous;
  const terminal=streamTerminalStatus(event);
  if(terminal==='completed'||terminal==='interrupted')return null;
  return previous;
}

/** The Web owner, native parent and exact turn must all match before a notice
 * changes. Unrelated operation errors live separately in useConversation. */
export function updateStreamRecovery(current:StreamRecoveryNotice|null,event:CodexNotification,ownerThreadId:string,nativeThreadId?:string,activeTurnId?:string,knownTerminalStatus?:StreamTerminalStatus):StreamRecoveryNotice|null {
  const previous=current?.ownerThreadId===ownerThreadId?current:null,threadId=upstreamThreadId(event),turnId=streamEventTurnId(event);
  if(!nativeThreadId||threadId!==nativeThreadId||!turnId||activeTurnId&&turnId!==activeTurnId)return previous;
  if(event.params.ownerThreadId!==undefined&&event.params.ownerThreadId!==ownerThreadId)return previous;
  const presentation=streamErrorPresentation(event);
  if(presentation&&(knownTerminalStatus==='completed'||knownTerminalStatus==='interrupted'||knownTerminalStatus==='failed'&&presentation.phase==='retrying'))return previous;
  if(presentation)return {ownerThreadId,threadId,turnId,...presentation};
  if(event.method==='reados/turnTerminal'&&event.params.reason==='sandbox-oom')return {ownerThreadId,threadId,turnId,phase:'failed',message:sandboxOomMessage};
  if(event.method==='turn/started'&&!knownTerminalStatus&&previous&&previous.turnId!==turnId)return null;
  if(!previous||previous.threadId!==threadId||previous.turnId!==turnId)return previous;
  const turn=object(event.params.turn);
  if(event.method==='turn/interrupted'||event.method==='reados/turnTerminal'&&event.params.status==='interrupted')return null;
  if(event.method==='turn/failed'||event.method==='turn/completed'&&turn.status==='failed'||event.method==='reados/turnTerminal'&&event.params.status==='failed')return {...previous,phase:'failed',message:failureMessage};
  if(event.method==='turn/completed')return null;
  if(previous.phase==='retrying'){
    const delta=['item/agentMessage/delta','item/reasoning/summaryTextDelta','item/reasoning/textDelta'].includes(event.method)&&typeof event.params.delta==='string'&&event.params.delta.length>0;
    const started=event.method==='item/started'&&['agentMessage','reasoning','commandExecution','mcpToolCall','webSearch','imageGeneration','fileChange'].includes(String(object(event.params.item).type));
    if(delta||started)return null;
  }
  return previous;
}
